<template>
  <div class="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
    <div class="flex items-start justify-between gap-4 mb-4">
      <div>
        <h3 class="text-lg font-semibold text-gray-800">
          Session Evaluation
        </h3>
        <p class="text-sm text-gray-500 mt-1">
          Last saved: {{ formattedLastSavedAt }}
        </p>
      </div>
      <BaseButton @click="save">Save</BaseButton>
    </div>

    <!-- Grade select -->
    <div class="mb-6">
      <label class="block font-semibold mb-1">Grade</label>
      <select
        v-model="gradeMode"
        class="border rounded-md p-2 w-full mb-2"
      >
        <option disabled value="">-- Select Grade Type --</option>
        <option value="userdefined">User defined</option>
        <option
          v-for="g in allowedGrades"
          :key="g"
          :value="g"
        >
          {{ g.toFixed(1) }}
        </option>
      </select>

      <!-- Custom grade input -->
      <div v-if="gradeMode === 'userdefined'" class="mt-2">
        <input
          type="number"
          v-model.number="customGrade"
          step="0.01"
          min="0"
          max="10"
          class="border rounded-md p-2 w-full"
          placeholder="Enter custom grade (e.g., 2.45)"
        />
      </div>

      <!-- Grade release toggle -->
      <div class="flex items-center gap-3 mt-3 pt-3 border-t border-gray-200">
        <label class="font-medium text-gray-700">Release Grade to Student</label>
        <label class="relative inline-flex items-center cursor-pointer">
          <input type="checkbox" v-model="internalGradeReleased" @change="saveReleaseFlag('grade_released', internalGradeReleased)" class="sr-only peer">
          <div class="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-indigo-600 transition-all"></div>
          <div class="absolute left-1 top-1 w-4 h-4 bg-white rounded-full shadow-md transform transition-all peer-checked:translate-x-5"></div>
        </label>
      </div>
    </div>

    <!-- Goal textarea -->
    <div class="mb-4">
      <label class="block font-semibold mb-1">Educational goal</label>
      <textarea
        v-model="internalGoal"
        rows="3"
        class="border rounded-md p-2 w-full"
        placeholder="Enter goal"
      ></textarea>
    </div>

    <!-- Evaluation textarea -->
    <div class="mb-6">
      <label class="block font-semibold mb-1">Evaluation</label>
      <textarea
        v-model="internalEvaluation"
        rows="4"
        class="border rounded-md p-2 w-full"
        placeholder="Enter evaluation"
      ></textarea>

      <!-- Evaluation release toggle -->
      <div class="flex items-center gap-3 mt-3 pt-3 border-t border-gray-200">
        <label class="font-medium text-gray-700">Release Evaluation to Student</label>
        <label class="relative inline-flex items-center cursor-pointer">
          <input type="checkbox" v-model="internalEvaluationReleased" @change="saveReleaseFlag('evaluation_released', internalEvaluationReleased)" class="sr-only peer">
          <div class="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-indigo-600 transition-all"></div>
          <div class="absolute left-1 top-1 w-4 h-4 bg-white rounded-full shadow-md transform transition-all peer-checked:translate-x-5"></div>
        </label>
      </div>
    </div>

    <!-- Recommendation textarea -->
    <div class="mb-6">
      <label class="block font-semibold mb-1">Recommendation</label>
      <textarea
        v-model="internalRecommendation"
        rows="4"
        class="border rounded-md p-2 w-full"
        placeholder="Enter recommendation"
      ></textarea>

      <!-- Recommendation release toggle -->
      <div class="flex items-center gap-3 mt-3 pt-3 border-t border-gray-200">
        <label class="font-medium text-gray-700">Release Recommendation to Student</label>
        <label class="relative inline-flex items-center cursor-pointer">
          <input type="checkbox" v-model="internalRecommendationReleased" @change="saveReleaseFlag('recommendation_released', internalRecommendationReleased)" class="sr-only peer">
          <div class="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-indigo-600 transition-all"></div>
          <div class="absolute left-1 top-1 w-4 h-4 bg-white rounded-full shadow-md transform transition-all peer-checked:translate-x-5"></div>
        </label>
      </div>
    </div>

    <div
      v-if="showSaveToast"
      class="fixed top-4 right-4 z-50 bg-green-600 text-white px-4 py-2 rounded-md shadow-lg"
      role="status"
      aria-live="polite"
    >
      Feedback saved successfully
    </div>
  </div>
</template>

<script>
import BaseButton from "@/BaseComponents/BaseButton.vue";

export default {
  name: "FeedbackSection",
  components: { BaseButton },
  props: {
    grade: { type: Number, default: null },
    goal: { type: String, default: "" },
    evaluation: { type: String, default: "" },
    recommendation: { type: String, default: "" },
    grade_released: { type: Boolean, default: false },
    evaluation_released: { type: Boolean, default: false },
    recommendation_released: { type: Boolean, default: false },
    lastSavedAt: { type: [String, Number, Date], default: null },
    saveSuccessTick: { type: Number, default: 0 }
  },
  emits: ["save", "dirty-change"],
  data() {
    return {
      internalGrade: this.grade,
      internalGoal: this.goal,
      internalEvaluation: this.evaluation,
      internalRecommendation: this.recommendation,
      internalGradeReleased: this.grade_released,
      internalEvaluationReleased: this.evaluation_released,
      internalRecommendationReleased: this.recommendation_released,
      allowedGrades: [1.0, 1.3, 1.7, 2.0, 2.3, 2.7, 3.0, 3.3, 3.7, 4.0, 5.0],
      gradeMode: "",
      customGrade: null,
      initialSnapshot: null,
      isDirty: false,
      showSaveToast: false,
      saveToastTimeoutId: null
    };
  },
  computed: {
    formattedLastSavedAt() {
      if (!this.lastSavedAt) {
        return "Not saved yet";
      }

      const parsedDate = this.parseAsLocalDisplayDate(this.lastSavedAt);
      if (Number.isNaN(parsedDate.getTime())) {
        return "Not saved yet";
      }

      return new Intl.DateTimeFormat("de-DE", {
        dateStyle: "short",
        timeStyle: "medium"
      }).format(parsedDate);
    }
  },
  watch: {
    grade(newVal) { 
      this.internalGrade = newVal;
      this.initializeGradeMode();
      this.refreshSnapshot();
    },
    goal(newVal) {
      this.internalGoal = newVal;
      this.refreshSnapshot();
    },
    evaluation(newVal) {
      this.internalEvaluation = newVal;
      this.refreshSnapshot();
    },
    recommendation(newVal) {
      this.internalRecommendation = newVal;
      this.refreshSnapshot();
    },
    grade_released(newVal) { this.internalGradeReleased = newVal; },
    evaluation_released(newVal) { this.internalEvaluationReleased = newVal; },
    recommendation_released(newVal) { this.internalRecommendationReleased = newVal; },
    gradeMode(newVal) {
      if (newVal !== "userdefined" && typeof newVal === "number") {
        this.internalGrade = newVal;
      }
    },
    customGrade(newVal) {
      if (this.gradeMode === "userdefined" && newVal !== null) {
        this.internalGrade = Math.round(newVal * 100) / 100;
      }
    },
    internalGrade() {
      this.updateDirtyState();
    },
    internalGoal() {
      this.updateDirtyState();
    },
    internalEvaluation() {
      this.updateDirtyState();
    },
    internalRecommendation() {
      this.updateDirtyState();
    },
    saveSuccessTick() {
      this.showSaveSuccessToast();
    },
    lastSavedAt(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.refreshSnapshot();
      }
    }
  },
  mounted() {
    this.initializeGradeMode();
    this.initializeSnapshot();
  },
  beforeUnmount() {
    if (this.saveToastTimeoutId) {
      clearTimeout(this.saveToastTimeoutId);
    }
  },
  methods: {
    parseAsLocalDisplayDate(rawTimestamp) {
      if (rawTimestamp instanceof Date) {
        return rawTimestamp;
      }

      if (typeof rawTimestamp === "number") {
        return new Date(rawTimestamp);
      }

      if (typeof rawTimestamp === "string") {
        const hasTimezone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(rawTimestamp.trim());
        const normalizedTimestamp = hasTimezone ? rawTimestamp : `${rawTimestamp}Z`;
        return new Date(normalizedTimestamp);
      }

      return new Date(rawTimestamp);
    },
    getCurrentSnapshot() {
      return {
        grade: this.internalGrade,
        goal: this.internalGoal,
        evaluation: this.internalEvaluation,
        recommendation: this.internalRecommendation
      };
    },
    initializeSnapshot() {
      this.initialSnapshot = this.getCurrentSnapshot();
      this.isDirty = false;
      this.$emit("dirty-change", false);
    },
    refreshSnapshot() {
      this.initialSnapshot = this.getCurrentSnapshot();
      this.updateDirtyState();
    },
    updateDirtyState() {
      if (!this.initialSnapshot) {
        return;
      }

      const currentSnapshot = this.getCurrentSnapshot();
      const dirty = JSON.stringify(currentSnapshot) !== JSON.stringify(this.initialSnapshot);

      if (dirty !== this.isDirty) {
        this.isDirty = dirty;
        this.$emit("dirty-change", dirty);
      }
    },
    showSaveSuccessToast() {
      this.showSaveToast = true;
      if (this.saveToastTimeoutId) {
        clearTimeout(this.saveToastTimeoutId);
      }
      this.saveToastTimeoutId = setTimeout(() => {
        this.showSaveToast = false;
      }, 3000);
    },
    initializeGradeMode() {
      if (this.internalGrade !== null) {
        if (this.allowedGrades.includes(this.internalGrade)) {
          this.gradeMode = this.internalGrade;
        } else {
          this.gradeMode = "userdefined";
          this.customGrade = this.internalGrade;
        }
      } else {
        this.gradeMode = "";
      }
    },
    saveReleaseFlag(fieldName, value) {
      // Emit partial save with only the changed release flag
      this.$emit("save", {
        [fieldName]: value
      });
    },
    save() {
      this.$emit("save", {
        grade: this.internalGrade,
        goal: this.internalGoal,
        evaluation: this.internalEvaluation,
        recommendation: this.internalRecommendation,
        grade_released: this.internalGradeReleased,
        evaluation_released: this.internalEvaluationReleased,
        recommendation_released: this.internalRecommendationReleased
      });
    }
  }
};
</script>
